/**
 * Gift Card Custom Amount — Product Page
 * Validates amount, gates Add to Cart,
 * updates price display, and injects _gift_card_amount into the theme's ATC request.
 */

class GiftCardAmountField {
  constructor(container) {
    this.container = container;
    this.min       = parseFloat(container.dataset.min);
    this.max       = parseFloat(container.dataset.max);
    this.form      = container.closest('form');
    this.submitBtn = this.form?.querySelector('[name="add"]');

    this.amountInput  = container.querySelector('.gift-card-amount__input');
    this.amountHidden = container.querySelector('[name="properties[_gift_card_amount]"]');
    this.amountError  = container.querySelector('.gift-card-amount__error');

    this._init();
  }

  _init() {
    this.amountInput.value = this.min;
    this._moveAboveQuantity();
    this._validateAmount();

    this.amountInput.addEventListener('keydown', (e) => this._blockDecimal(e));
    this.amountInput.addEventListener('input',   () => this._stripDecimal());
    this.amountInput.addEventListener('input',   () => this._validateAmount());
    this.amountInput.addEventListener('blur',    () => this._validateAmount());
    this.form?.addEventListener('submit', (e) => this._onSubmit(e));
  }

  _moveAboveQuantity() {
    const qtyEl = document.querySelector('.apl-section-main-product-form-quantity, .product-form__quantity');
    if (!qtyEl) return;
    const formId = this.form?.id;
    if (formId) {
      this.container.querySelectorAll('input[type="hidden"]').forEach(el => {
        el.setAttribute('form', formId);
      });
    }
    qtyEl.parentNode.insertBefore(this.container, qtyEl);
  }

  _validateAmount() {
    const raw   = this.amountInput.value.trim();
    const value = parseFloat(raw);

    if (!raw || isNaN(value)) {
      this._setAmountError('Please enter a gift card amount.');
      this.amountHidden.value = '';
      this._updatePagePrice(null); // restore original price
      this._setSubmitDisabled(true);
      return false;
    }
    if (value < this.min) {
      this._setAmountError(`Minimum amount is RM${this.min.toLocaleString()}.`);
      this.amountHidden.value = '';
      this._updatePagePrice(null); // restore original price
      this._setSubmitDisabled(true);
      return false;
    }
    if (value > this.max) {
      this._setAmountError(`Maximum amount is RM${this.max.toLocaleString()}.`);
      this.amountHidden.value = '';
      this._updatePagePrice(null); // restore original price
      this._setSubmitDisabled(true);
      return false;
    }

    this._clearAmountError();
    this.amountHidden.value = value.toFixed(2);
    this._updatePagePrice(value);
    this._setSubmitDisabled(false);
    return true;
  }

  _blockDecimal(e) {
    if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
  }

  _stripDecimal() {
    const val = this.amountInput.value;
    if (val.includes('.')) {
      this.amountInput.value = val.split('.')[0];
    }
  }

  _onSubmit(e) {
    if (!this._validateAmount()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.amountInput.focus();
    }
  }

  _setAmountError(msg) {
    this.amountError.textContent = msg;
    this.amountError.removeAttribute('hidden');
    this.amountInput.setAttribute('aria-invalid', 'true');
  }

  _clearAmountError() {
    this.amountError.textContent = '';
    this.amountError.setAttribute('hidden', '');
    this.amountInput.removeAttribute('aria-invalid');
  }

  _setSubmitDisabled(disabled) {
    document.querySelectorAll('[name="add"], .footer-atc').forEach(btn => {
      btn.disabled = disabled;
      btn.classList.toggle('apl-section-soldout-button', disabled);
      btn.classList.toggle('apl-section-atc-button', !disabled);
    });
  }

  _updatePagePrice(amount) {
    const formatted = amount !== null
      ? `RM${amount.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : null;

    // Standard price elements
    const selectors = [
      '.price-item--regular',
      '[data-regular-price]',
      '.price__regular .money',
      '.price .money',
    ];
    document.querySelectorAll(selectors.join(',')).forEach(el => {
      if (!el.dataset.originalText) el.dataset.originalText = el.textContent.trim();
      el.textContent = formatted ?? el.dataset.originalText;
      el.style.visibility = '';
    });

    // Sticky bar / main price element (exclude NMP span)
    document.querySelectorAll('.js-product-price-with-care-warranty:not(.js-nmp-price)').forEach(el => {
      if (!el.dataset.originalText) el.dataset.originalText = el.textContent.trim();
      if (!el.dataset.originalPrice) el.dataset.originalPrice = el.dataset.price || '';
      if (!el.dataset.originalPriceRaw) el.dataset.originalPriceRaw = el.dataset.priceRaw || '';

      if (amount !== null) {
        el.textContent = formatted;
        el.dataset.price = formatted;
        el.dataset.priceWithoutCurrency = amount.toFixed(2);
        el.dataset.priceRaw = Math.round(amount * 100);
      } else {
        el.textContent = el.dataset.originalText;
        el.dataset.price = el.dataset.originalPrice;
        el.dataset.priceRaw = el.dataset.originalPriceRaw;
      }
    });

    // Net monthly pricing — update with amount / 36
    const priceCalc = parseFloat(
      document.querySelector('.js-product-price-with-care-warranty:not(.js-nmp-price)')?.dataset.priceCalculation
    ) || 36;
    document.querySelectorAll('.js-nmp-price').forEach(el => {
      if (!el.dataset.originalText) el.dataset.originalText = el.textContent.trim();
      if (amount !== null) {
        const monthly = amount / priceCalc;
        el.textContent = `RM${monthly.toFixed(2).replace(/\.?0+$/, '')}`;
      } else {
        el.textContent = el.dataset.originalText;
      }
    });
  }
}

// ---------------------------------------------------------------------------
// Cart/add interceptor — inject _gift_card_amount into theme's ATC request
// ---------------------------------------------------------------------------
(function patchCartAdd() {
  function getGiftCardProps() {
    const container = document.querySelector('.gift-card-amount');
    if (!container) return null; // not a gift card page — don't interfere

    const amount = document.querySelector('[name="properties[_gift_card_amount]"]')?.value;
    if (!amount || parseFloat(amount) <= 0) return false; // gift card page but invalid amount — block
    return { '_gift_card_amount': amount };
  }

  function injectIntoBody(body, props) {
    if (body instanceof FormData) {
      const fd = new FormData();
      for (const [k, v] of body.entries()) fd.append(k, v);
      Object.entries(props).forEach(([k, v]) => fd.set(`properties[${k}]`, v));
      return fd;
    }
    if (typeof body === 'string') {
      try {
        const data = JSON.parse(body);
        if (Array.isArray(data.items)) {
          data.items = data.items.map(item => ({
            ...item,
            properties: Object.assign({}, item.properties, props),
          }));
        } else {
          data.properties = Object.assign({}, data.properties, props);
        }
        return JSON.stringify(data);
      } catch (_) { return body; }
    }
    return body;
  }

  function isCartAdd(url) {
    return typeof url === 'string' && url.includes('/cart/add');
  }

  function blockResponse() {
    return new Response(JSON.stringify({ errors: 'Please enter a valid gift card amount.' }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  function focusAmountInput() {
    const input = document.querySelector('.gift-card-amount__input');
    if (input) { input.focus(); input.select(); }
  }

  // Patch fetch
  const _fetch = window.fetch.bind(window);
  window.fetch = async function (resource, options, ...rest) {
    const url = typeof resource === 'string' ? resource : (resource?.url || '');
    if (isCartAdd(url)) {
      const props = getGiftCardProps();
      if (props === false) { focusAmountInput(); return blockResponse(); }
      if (props && options?.body) {
        options = { ...options, body: injectIntoBody(options.body, props) };
      }
    }
    return _fetch(resource, options, ...rest);
  };

  // Patch XMLHttpRequest
  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._xhrUrl = url || '';
    return _open.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (isCartAdd(this._xhrUrl)) {
      const props = getGiftCardProps();
      if (props === false) { focusAmountInput(); this.abort(); return; }
      if (props && body) body = injectIntoBody(body, props);
    }
    return _send.call(this, body);
  };
})();

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.gift-card-amount').forEach(el => {
    new GiftCardAmountField(el);
  });
});
